import { formatDate } from "@sembark-travel/datetime-utils"
import {
	Alert,
	Box,
	Button,
	Col,
	DeferRender,
	Divider,
	Grid,
	Icons,
	Inline,
	Money,
	Spinner,
	Stack,
	Table,
	TableDataCell,
	Text,
	Time,
} from "@sembark-travel/ui/base"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import {
	Form,
	GetFieldValue,
	isTruthy,
	SelectField,
	SwitchInputField,
	TextInputField,
	withServerErrors,
} from "@sembark-travel/ui/form"
import { Markdown } from "@sembark-travel/ui/markdown"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import React, { useCallback, useEffect, useRef } from "react"
import useSWR from "swr"
import config from "../config"
import {
	ITermsAndCondition,
	SelectTermsAndConditions,
} from "../TermsAndConditions"
import { IPaymentReceipt } from "./store"

function XHR(xhr: XHRInstance) {
	return {
		async generatePaymentReceipt(
			instalmentId: number | string,
			params: unknown
		) {
			return xhr
				.get<{
					data: IPaymentReceipt
				}>(`/instalments/${instalmentId}/payment-receipts/create`, { params })
				.then((resp) => resp.data.data)
		},
	}
}

function DefaultGenerateReceiptRender({
	onGenerate,
}: {
	onGenerate: () => void
}) {
	return <Button onClick={onGenerate}>Regenerate Receipt</Button>
}

export function GenerateReceiptAssets({
	receiptId,
	onChange,
	children = DefaultGenerateReceiptRender,
}: {
	receiptId: number
	onChange: () => void
	children?: (
		props: React.ComponentProps<typeof DefaultGenerateReceiptRender>
	) => React.ReactNode
}) {
	const xhr = useXHR()
	const generate = useCallback(() => {
		xhr
			.post("/generate-payment-receipts", {
				items: [receiptId],
				timezone_offset: config.timezoneOffset,
			})
			.then(() => {
				onChange()
			})
	}, [receiptId, xhr, onChange])
	return <>{children({ onGenerate: generate })}</>
}

export function GeneratedReceiptDownload({
	id,
	isGenerating,
	fileUrl,
	onChange,
	size,
	...props
}: {
	id: number
	isGenerating?: boolean
	fileUrl?: string
	onChange: () => void
} & Omit<React.ComponentProps<typeof Box>, "id" | "onChage">) {
	const xhr = useXHR()
	const { data } = useSWR<{
		done: boolean
		in_progress: boolean
		failed: boolean
	}>(
		isGenerating ? `/payment-receipt-generation-status/${id}` : null,
		(key) => xhr.get(key).then((resp) => resp.data.data),
		{
			refreshInterval: isGenerating ? 2000 : 0,
		}
	)
	// cache the onchange
	const onChangeRef = useRef(onChange)
	onChangeRef.current = onChange
	useEffect(() => {
		if (data) {
			if (data.in_progress === false && isGenerating) {
				onChangeRef.current()
			}
		}
	}, [data, isGenerating])
	return isGenerating ? (
		<Button disabled inline title="Generating receipt" {...props}>
			<Spinner />
		</Button>
	) : fileUrl ? (
		<Button<"a">
			as="a"
			inline
			href={fileUrl}
			title="Download Receipt"
			target="_blank"
			rel="noopener noreferrer"
			{...props}
		>
			<Button status="success" level="primary">
				<Icons.DocumentDownload />
				Download Receipt
			</Button>
		</Button>
	) : (
		<GenerateReceiptAssets onChange={onChange} receiptId={id}>
			{({ onGenerate }) => (
				<Button type="button" onClick={onGenerate} {...props} inline>
					<Icons.Attention color={"warning"} title="File not generated" />
				</Button>
			)}
		</GenerateReceiptAssets>
	)
}

export function GeneratedReceiptAsset({
	id,
	isGenerating,
	fileUrl,
	onChange,
	size,
	...props
}: {
	id: number
	isGenerating?: boolean
	fileUrl?: string
	onChange: () => void
} & Omit<React.ComponentProps<typeof Box>, "id" | "onChage">) {
	const xhr = useXHR()
	const { data } = useSWR<{
		done: boolean
		in_progress: boolean
		failed: boolean
	}>(
		isGenerating ? `/payment-receipt-generation-status/${id}` : null,
		(key) => xhr.get(key).then((resp) => resp.data.data),
		{
			refreshInterval: isGenerating ? 2000 : 0,
		}
	)
	// cache the onchange
	const onChangeRef = useRef(onChange)
	onChangeRef.current = onChange
	useEffect(() => {
		if (data) {
			if (data.in_progress === false && isGenerating) {
				onChangeRef.current()
			}
		}
	}, [data, isGenerating])
	return isGenerating ? (
		<Button disabled inline title="Generating receipt" {...props}>
			<Spinner />
		</Button>
	) : fileUrl ? (
		<Button<"a">
			as="a"
			inline
			href={fileUrl}
			title="Download Receipt"
			target="_blank"
			rel="noopener noreferrer"
			{...props}
		>
			<Text color="accent">
				<Icons.DocumentDownload />
			</Text>
		</Button>
	) : (
		<GenerateReceiptAssets onChange={onChange} receiptId={id}>
			{({ onGenerate }) => (
				<Button type="button" onClick={onGenerate} {...props} inline>
					<Icons.Attention color={"warning"} title="File not generated" />
				</Button>
			)}
		</GenerateReceiptAssets>
	)
}

export function PaymentReceiptDialog({
	children,
	instalmentId,
	onSuccess,
}: {
	instalmentId: number | string
	onSuccess?: () => void
	children: (props: { create: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ create: open })}
			<Dialog open={isOpen} onClose={close} title="Payment Receipt">
				<PaymentReceipt
					instalmentId={instalmentId}
					onSuccess={() => {
						onSuccess?.()
						close()
					}}
					onCancel={() => close()}
				/>
			</Dialog>
		</>
	)
}

function PaymentReceipt({
	instalmentId,
	onSuccess,
	onCancel,
}: {
	instalmentId: number | string
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const { data: receipt } = useSWR(
		`/instalments/${instalmentId}/payment-receipts/create`,
		() =>
			XHR(xhr).generatePaymentReceipt(instalmentId, {
				timezone_offset: config.timezoneOffset,
			})
	)
	if (!receipt) {
		return <Spinner alignCenter padding="4" />
	}

	const {
		receipt_date,
		reference_id,
		credit_account_name,
		debit_account_name,
		received_amount,
		total_payable_amount,
		total_received_amount,
		currency,
		total_received_verified_amount,
	} = receipt
	return (
		<Form<{
			terms_and_conditions: ITermsAndCondition
			mode_of_deposit: string
			exclude_account_name?: 0 | 1
		}>
			initialValues={{
				terms_and_conditions: receipt.terms_and_conditions,
				mode_of_deposit: receipt.mode_of_deposit,
				exclude_account_name: isTruthy(receipt.exclude_account_name) ? 1 : 0,
			}}
			onSubmit={withServerErrors(async (values) => {
				const data = {
					mode_of_deposit: values.mode_of_deposit,
					terms_and_conditions: values.terms_and_conditions
						? values.terms_and_conditions.id
						: null,
					exclude_account_name: isTruthy(values.exclude_account_name) ? 1 : 0,
					generated_at_local: formatDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
					timezone_offset: config.timezoneOffset,
				}
				await xhr.post(`/instalments/${instalmentId}/payment-receipts`, data)
				showSnackbar("Receipt generated successfully.")
				onSuccess()
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form onSubmit={handleSubmit} noValidate>
					<Dialog.Body>
						<Stack gap={"4"}>
							<Grid gap="4" alignItems={"center"}>
								<Col sm={5}>
									<TextInputField
										label="Mode of Payment"
										name="mode_of_deposit"
										placeholder="eg: NEFT, UPI, etc."
										secondaryLabel="optional"
										size="sm"
									/>
								</Col>
								<Col gap={"2"}>
									<Stack gap={"1"}>
										<SwitchInputField
											label="Exclude Credit Account"
											name="exclude_account_name"
											value={"1"}
											color={"muted"}
										/>
										<GetFieldValue<0 | 1> name="exclude_account_name">
											{({ value }) => (
												<Box
													borderWidth={"1"}
													borderRadius={"md"}
													borderColor={"emphasis"}
													paddingY={"1"}
													paddingX={"2"}
													bgColor={value ? "gray2" : "default"}
												>
													{credit_account_name}
												</Box>
											)}
										</GetFieldValue>
									</Stack>
								</Col>
							</Grid>
							<SelectField
								label="Receipt terms and conditions"
								fetchOnMount
								select={SelectTermsAndConditions}
								name="terms_and_conditions"
								placeholder="Applied terms and conditions for this package"
								secondaryLabel="optional"
								size="sm"
							/>
							<GetFieldValue<ITermsAndCondition> name="terms_and_conditions">
								{({ value: terms_and_conditions }) =>
									terms_and_conditions ? (
										<Stack gap="2">
											{terms_and_conditions.deleted_at ? (
												<Alert
													status="warning"
													title="These terms have been archived"
												>
													Please select a diffent terms to align better with
													company policies.
												</Alert>
											) : null}
											<Box
												padding="4"
												borderWidth="1"
												rounded="md"
												style={{ maxHeight: "100px" }}
												overflow="auto"
												bgColor="subtle"
											>
												<DeferRender>
													<Markdown>
														{terms_and_conditions.description}
													</Markdown>
												</DeferRender>
											</Box>
										</Stack>
									) : null
								}
							</GetFieldValue>
							<Divider sm />

							<Stack gap="4">
								<Text color="muted">
									Please verify payment details that will be used in Receipt.
								</Text>
								<Table bordered responsive striped>
									<tbody>
										<tr>
											<TableDataCell>
												<Text fontWeight={"semibold"}>Payment Date</Text>
											</TableDataCell>
											<TableDataCell>
												<Time timestamp={receipt_date} format="DD MMM YYYY" />
											</TableDataCell>
										</tr>
										<tr>
											<TableDataCell>
												<Text fontWeight={"semibold"}>Amount Paid</Text>
											</TableDataCell>
											<TableDataCell>
												<Box>
													{currency}
													{" : "}
													<Money currency={currency} amount={received_amount} />
												</Box>
											</TableDataCell>
										</tr>
										{reference_id ? (
											<tr>
												<TableDataCell>
													<Text fontWeight={"semibold"}>Reference Id</Text>
												</TableDataCell>
												<TableDataCell>
													<Text>{reference_id}</Text>
												</TableDataCell>
											</tr>
										) : null}
										<tr>
											<TableDataCell>
												<Text fontWeight={"semibold"}>Paid By</Text>
											</TableDataCell>
											<TableDataCell>
												<Text>{debit_account_name}</Text>
											</TableDataCell>
										</tr>
										<GetFieldValue<0 | 1> name="exclude_account_name">
											{({ value }) =>
												!isTruthy(value) ? (
													<tr>
														<TableDataCell>
															<Text fontWeight={"semibold"}>
																Credit Account
															</Text>
														</TableDataCell>
														<TableDataCell>{credit_account_name}</TableDataCell>
													</tr>
												) : null
											}
										</GetFieldValue>

										<tr>
											<TableDataCell>
												<Text fontWeight={"semibold"}>Payment status</Text>

												<Text fontSize="sm" color="muted">
													(Including this payment)
												</Text>
											</TableDataCell>
											<TableDataCell>
												<Inline gap={"2"} alignItems={"center"}>
													{currency}
													{" : "}
													<Money
														amount={total_received_verified_amount}
														currency={currency}
													/>
													{"/"}
													<Money
														amount={total_payable_amount}
														currency={currency}
													/>
													{Number(total_payable_amount) ===
													Number(total_received_verified_amount) ? (
														<Text color="success" fontSize="sm">
															<Icons.BadgeCheckSolid />
															Payment Clear
														</Text>
													) : null}
												</Inline>
												{Number(total_received_verified_amount) !==
												Number(total_received_amount) ? (
													<Text fontSize="sm" color="muted">
														(Includes only verified payments)
													</Text>
												) : null}
											</TableDataCell>
										</tr>
									</tbody>
								</Table>
							</Stack>
						</Stack>
					</Dialog.Body>
					<Dialog.Footer>
						<Button type="submit" disabled={submitting}>
							{submitting ? "Generating..." : "Generate Receipt"}
						</Button>
						<Button
							level="tertiary"
							onClick={() => onCancel()}
							disabled={submitting}
						>
							Close
						</Button>
					</Dialog.Footer>
				</form>
			)}
		</Form>
	)
}
